import { Input, inputVariants } from '../Input';
import type { InputProps } from '../Input';
import { cn } from '@thinkalpha/ui-core/utils';
import type { VariantProps } from '@thinkalpha/ui-core/utils';
import { forwardRef } from 'react';
import MaskedInput from 'react-currency-input-field';
import type { CurrencyInputProps } from 'react-currency-input-field';

export interface MaskedNumberProps
    extends
        Omit<InputProps, 'onChange' | 'step' | 'value' | 'defaultValue'>,
        Omit<CurrencyInputProps, 'onChange' | 'onValueChange' | 'value' | 'defaultValue'>,
        VariantProps<typeof inputVariants> {
    /**
     *  It can parse values with abbreviations k, m and b
     *
     *     Examples:
     *
     *     1k = 1,000
     *     2.5m = 2,500,000
     *     3.456B = 3,456,000,000
     *
     *     This can be turned off by passing in disableAbbreviations
     *
     */
    disableAbbreviations?: boolean;
    /**
     * The current value
     */
    value?: number | string | undefined;
    /**
     * Default value when uncontrolled
     */
    defaultValue?: number | undefined;
    /**
     * onChange callback
     */
    onChange: (value: string) => void;
    /**
     * suffix to display after the number
     */
    suffix?: string;
}

export const MaskedNumber = forwardRef<HTMLInputElement, MaskedNumberProps>(
    ({ value, defaultValue, onChange, suffix, prefix, className, variant, dimensions, ...props }, ref) => {
        return (
            <MaskedInput
                customInput={Input}
                ref={ref}
                value={typeof value === 'string' ? value : value?.toString()}
                defaultValue={defaultValue?.toString()}
                onValueChange={(value) => {
                    onChange(value ?? '');
                }}
                prefix={prefix}
                suffix={suffix}
                className={cn(inputVariants({ variant, dimensions, className }))}
                {...props}
            />
        );
    },
);

MaskedNumber.displayName = 'MaskedNumber';
